import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const auth = useAuthInternal();
  return <AuthCtx.Provider value={auth}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

const useAuthInternal = () => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [dealerId, setDealerId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verify dealer_id exists in dealers table; if not, look up by email
  const verifyDealerId = async (dId, email) => {
    if (!dId) return null;
    try {
      const { data } = await supabase.from('dealers').select('id').eq('id', dId).maybeSingle();
      if (data) return dId; // valid
    } catch (e) { /* not found */ }

    // dealer_id invalid — try to find correct dealer by email
    if (email) {
      try {
        const { data: dealer } = await supabase
          .from('dealers')
          .select('id')
          .eq('email', email)
          .maybeSingle();
        if (dealer) {
          console.log('[Auth] Auto-corrected dealer_id from', dId, 'to', dealer.id);
          return dealer.id;
        }
      } catch (e) { /* no match */ }

      // Also check dealer_invitations as fallback
      try {
        const { data: invite } = await supabase
          .from('dealer_invitations')
          .select('dealer_id')
          .eq('email', email)
          .eq('status', 'accepted')
          .maybeSingle();
        if (invite?.dealer_id) {
          console.log('[Auth] Resolved dealer_id from invitation:', invite.dealer_id);
          return invite.dealer_id;
        }
      } catch (e) { /* no match */ }
    }
    return dId; // return original if no correction found
  };

  const fetchUserRole = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role, dealer_id')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) {
        // Set role immediately — don't block login
        setUserRole(data.role);
        setDealerId(data.dealer_id);

        // Verify dealer link in background (non-blocking)
        const isDealer = data.role === 'dealer_admin' || data.role === 'dealer_staff';
        if (isDealer && data.dealer_id) {
          verifyDealerId(data.dealer_id, null).then(async (resolved) => {
            if (resolved && resolved !== data.dealer_id) {
              setDealerId(resolved);
              await supabase.from('user_profiles').update({ dealer_id: resolved }).eq('id', userId);
            }
          }).catch(() => {});
        }

        return data.role;
      }

      // Profile missing — create one from user_metadata (covers dealer onboarding gap)
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const meta = currentUser?.user_metadata;
      if (meta && (meta.shedx_role || meta.role)) {
        const role = meta.shedx_role || meta.role || 'dealer_admin';
        const dId = meta.dealer_id || null;

        // Set role immediately — don't block login
        setUserRole(role);
        setDealerId(dId);

        // Persist profile + verify dealer_id in background
        const bgWork = async () => {
          let resolvedDId = dId;
          const isDealer = role === 'dealer_admin' || role === 'dealer_staff';
          if (isDealer && dId) {
            resolvedDId = await verifyDealerId(dId, currentUser.email);
            if (resolvedDId !== dId) setDealerId(resolvedDId);
          } else if (isDealer && !dId) {
            // No dealer_id in metadata — try to find by email
            resolvedDId = await verifyDealerId(null, currentUser.email);
            if (resolvedDId) setDealerId(resolvedDId);
          }
          try {
            await supabase.from('user_profiles').upsert([{
              id: userId,
              email: currentUser.email,
              role: role,
              dealer_id: resolvedDId
            }]);
          } catch (e) {
            console.warn('Could not auto-create user profile:', e);
          }
        };
        bgWork().catch(() => {});

        return role;
      }
    } catch (err) {
      console.log('Could not fetch user profile, defaulting role');
    }
    return null;
  };

  useEffect(() => {
    // Check for local bypass session first
    const bypassUser = localStorage.getItem('adminBypassUser');
    if (bypassUser) {
      try {
        const parsed = JSON.parse(bypassUser);
        setUser(parsed);
        setUserRole(parsed.user_metadata?.shedx_role || 'master_admin');
        setDealerId(parsed.user_metadata?.dealer_id || null);
        setLoading(false);
        return;
      } catch (e) {
        localStorage.removeItem('adminBypassUser');
      }
    }

    // Get initial session from Supabase
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        await fetchUserRole(currentUser.id);
      }
      setLoading(false);
    };

    getSession();

    // Listen for auth changes — never await DB calls here (deadlocks signInWithPassword)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          fetchUserRole(currentUser.id).catch(() => {});
        } else if (event === 'SIGNED_OUT') {
          setUserRole(null);
          setDealerId(null);
        }
        setLoading(false);
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    setLoading(true);
    
    try {
      // Fallback: local admin bypass
      const bypassPassword = import.meta.env.VITE_ADMIN_BYPASS_PASSWORD;
      const adminEmails = (import.meta.env.VITE_ADMIN_EMAILS || '')
        .split(',')
        .map(e => e.trim().toLowerCase())
        .filter(Boolean);
      
      if (bypassPassword && password === bypassPassword && adminEmails.includes(email.toLowerCase())) {
        const mockUser = {
          id: 'local-admin',
          email: email.toLowerCase(),
          user_metadata: { role: 'admin', shedx_role: 'master_admin' },
          app_metadata: { role: 'admin' }
        };
        setUserRole('master_admin');
        setDealerId(null);
        setUser(mockUser);
        localStorage.setItem('adminBypassUser', JSON.stringify(mockUser));
        setLoading(false);
        return { data: { user: mockUser }, error: null };
      }

      // Supabase auth with timeout
      console.log('[Auth] Attempting signInWithPassword...');
      const authPromise = supabase.auth.signInWithPassword({ email, password });
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Login timed out. Please check your connection and try again.')), 30000)
      );

      const { data, error } = await Promise.race([authPromise, timeout]);
      console.log('[Auth] signInWithPassword result:', error ? error.message : 'success');

      if (!error) {
        if (data?.user) {
          console.log('[Auth] Fetching user role...');
          await fetchUserRole(data.user.id);
          console.log('[Auth] Role fetched');
        }
        setLoading(false);
        return { data, error: null };
      }
      
      setLoading(false);
      
      if (error?.message?.includes('captcha')) {
        return { 
          data: null, 
          error: { message: 'Please try again in a few moments or contact support if the issue persists.' } 
        };
      }
      
      return { data, error };
    } catch (err) {
      console.error('[Auth] signIn error:', err);
      setLoading(false);
      return { 
        data: null, 
        error: { message: err.message || 'Authentication failed. Please check your credentials.' } 
      };
    }
  };

  const signOut = async () => {
    localStorage.removeItem('adminBypassUser');
    const { error } = await supabase.auth.signOut();
    setUser(null);
    setUserRole(null);
    setDealerId(null);
    return { error };
  };

  const isMasterAdmin = () => userRole === 'master_admin';
  const isDealerAdmin = () => userRole === 'dealer_admin';
  const isDealerStaff = () => userRole === 'dealer_staff';

  const isAdmin = () => {
    const envEmails = (import.meta.env.VITE_ADMIN_EMAILS || '')
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    const defaultEmails = ['info@shedx.com', 'jbrous96@gmail.com', 'jbrous96610@gmail.com', 'seleadership2@gmail.com'];
    const adminEmails = envEmails.length ? envEmails : defaultEmails;
    const email = user?.email?.toLowerCase();
    return Boolean(
      (email && adminEmails.includes(email)) ||
      user?.user_metadata?.role === 'admin' ||
      user?.app_metadata?.role === 'admin'
    );
  };

  return {
    user,
    loading,
    userRole: userRole || (isAdmin() ? 'master_admin' : null),
    dealerId,
    signIn,
    signOut,
    isAdmin,
    isMasterAdmin,
    isDealerAdmin,
    isDealerStaff,
  };
};